import type {
  CurveDef,
  MorphOptions,
  Point,
  RendererOptions,
  RuntimeRenderOptions,
  SarmalInstance,
  TrailColor,
  TrailStyle,
} from "./types";
export type { TrailPoint } from "./renderer-shared";
export type { Rgb } from "./renderer-shared";
export type { Oklab } from "./renderer-shared";

import {
  DEFAULT_MORPH_DURATION_MS,
  DEFAULT_SKELETON_OPACITY,
  DESTROYED_ERROR,
  TRAIL_MIN_WIDTH,
  TRAIL_MAX_WIDTH,
  easeInOutCubic,
  computeBoundaries,
  computeTrailQuad,
  enginePassthroughs,
  colorToRgb,
  getPaletteColor,
  parseColorToOklab,
  resolveHeadColor,
  resolveTrailPalette,
  resolveTrailMainColor,
  oklabToRgb,
  Oklab,
  validateRenderOptions,
  warnIfTrailColorMismatch,
} from "./renderer-shared";

const getHeadDotRadius = (w: number, h: number) => Math.max(1, 3 * Math.sqrt(Math.min(w, h) / 160));

export { computeTangent, computeNormal } from "./renderer-shared";
export { palettes, lerpOklab, getPaletteColor, parseColorToRgb } from "./renderer-shared";

const WHITE_HEX = "#ffffff";

export function colorToRgbComponents(color: string): string {
  const { r, g, b } = colorToRgb(color);
  return `${r},${g},${b}`;
}

/**
 * ! Exported purely so `applyDprSizing` can be unit-tested without a DOM
 */
export interface DprSizingTarget {
  width: number;
  height: number;
  style: { width: string; height: string };
}

/**
 * Applies DPR sizing to a target
 *
 * ! DO NOT REMOVE THE `style.width` and `style.height` ASSIGNMENTS
 *
 * A canvas with only `width`/`height` HTML attributes and no CSS derives
 *  its displayed size from those attributes.
 * If we set `canvas.width = logicalWidth * dpr` to scale the drawing buffer for "crispness",
 *  the element ALSO visually grows by the DPR factor
 * ! The bug is silent and sneaky. It just resulsts in a broken render without any obvious reasons
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Optimizing_canvas
 *      MDN: "If you don't set the CSS attributes, the intrinsic size of the canvas will be used as its display size"
 *
 * @see https://webglfundamentals.org/webgl/lessons/webgl-resizing-the-canvas.html
 *      "Every canvas has 2 sizes: the drawingbuffer (pixels) and the display
 *        size (CSS). If no CSS affects display size, it equals drawingbuffer."
 *
 * @see https://stackoverflow.com/questions/4938346/canvas-width-and-height-in-html5
 *      Phrogz: "If you don't set the CSS attributes, the intrinsic size of
 *        the canvas will be used as its display size."
 *
 * The regression test for this lives in **renderer.test.ts**
 */
export function applyDprSizing(
  target: DprSizingTarget,
  logicalWidth: number,
  logicalHeight: number,
  dpr: number,
): void {
  // Pin the CSS display size FIRST so changing the attributes below
  // cannot resize the element.
  target.style.width = `${logicalWidth}px`;
  target.style.height = `${logicalHeight}px`;

  target.width = logicalWidth * dpr;
  target.height = logicalHeight * dpr;
}

/**
 * Creates a Canvas 2D renderer for sarmal animations
 * Renders the skeleton and the trail
 */
export function createRenderer(options: RendererOptions): SarmalInstance {
  const canvas = options.canvas;
  if (!canvas.getContext("2d")) {
    throw new Error("Could not get 2d context from canvas");
  }
  const ctx = canvas.getContext("2d")!;

  const engine = options.engine;

  // ! Mutated only by `setRenderOptions`.
  let trailStyle: TrailStyle = options.trailStyle ?? "default";
  let trailColor: TrailColor = options.trailColor ?? WHITE_HEX;
  let skeletonColor: string = options.skeletonColor ?? WHITE_HEX;
  // `null` means that head color is derived from `trailColor` & `trailStyle` and refreshed whenever either changes.
  let userHeadColor: string | null = options.headColor ?? null;
  let headColor: string = userHeadColor ?? resolveHeadColor(trailColor, trailStyle);

  let trailSolidRgb = colorToRgbComponents(resolveTrailMainColor(trailColor));
  let trailPalette = resolveTrailPalette(trailColor);
  let trailPaletteOklab: Array<Oklab> = trailPalette.map((c) => parseColorToOklab(c)!);

  warnIfTrailColorMismatch(trailColor, trailStyle);

  /**
   * Device pixel ratio for high-DPI displays.
   * We scale the canvas buffer size by DPR and apply a transform scale
   */
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;

  /**
   * Sets up the canvas for DPR scaling
   */
  function setupCanvas() {
    // TODO: attach a ResizeObserver so this function can be called again for the right scale of canvas
    const lw = canvas.offsetWidth || 200;
    const lh = canvas.offsetHeight || 200;
    applyDprSizing(canvas, lw, lh, dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  setupCanvas();

  // Store logical dimensions for boundary calculations
  let logicalWidth = canvas.width / dpr;
  let logicalHeight = canvas.height / dpr;
  if (options.headRadius !== undefined) {
    validateRenderOptions({ headRadius: options.headRadius });
  }

  if (options.trailWidth !== undefined) {
    validateRenderOptions({ trailWidth: options.trailWidth });
  }

  let headRadius = options.headRadius ?? getHeadDotRadius(logicalWidth, logicalHeight);
  let trailWidth = options.trailWidth ?? 1;

  let skeleton: Array<Point> = [];
  let skeletonCanvas: OffscreenCanvas | null = null;
  let trail: Array<Point> = [];
  let trailCount = 0;
  let head: Point | null = null;
  let scale = 1;
  let offsetX = 0;
  let offsetY = 0;
  let animationId: number | null = null;
  let lastTime = 0;
  let pausedByVisibility = false;
  let destroyed = false;

  let morphResolve: (() => void) | null = null;
  let morphReject: ((error: Error) => void) | null = null;
  let morphDurationMs = DEFAULT_MORPH_DURATION_MS;
  // Raw linear progress of the morph over time (0 to 1), used for timing and completion.
  // The eased value derived from it is what actually drives the blend via setMorphAlpha.
  let morphProgress = 0;
  let morphEasingFn: (t: number) => number = easeInOutCubic;

  /** Accumulated time for "gradient-animated" trail style */
  let gradientAnimTime = 0;

  function calculateBoundaries() {
    const b = computeBoundaries(skeleton, logicalWidth, logicalHeight);
    if (b) {
      scale = b.scale;
      offsetX = b.offsetX;
      offsetY = b.offsetY;
    }
  }

  /**
   * Draws the skeleton once into an OffscreenCanvas so that every frame
   * only needs a single ctx.drawImage() instead of rebuilding the full path.
   */
  function buildSkeletonCanvas() {
    if (skeleton.length < 2) {
      return;
    }

    skeletonCanvas = new OffscreenCanvas(canvas.width, canvas.height);
    const skeletonCtx = skeletonCanvas.getContext("2d")!;

    // Apply DPR scale to draw in logical coordinates
    skeletonCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

    skeletonCtx.strokeStyle = `rgba(${colorToRgbComponents(skeletonColor)},${DEFAULT_SKELETON_OPACITY})`;
    skeletonCtx.lineWidth = 1.5;
    skeletonCtx.beginPath();

    const first = skeleton[0]!;
    skeletonCtx.moveTo(first.x * scale + offsetX, first.y * scale + offsetY);

    for (let i = 1; i < skeleton.length; i++) {
      const p = skeleton[i]!;
      skeletonCtx.lineTo(p.x * scale + offsetX, p.y * scale + offsetY);
    }

    skeletonCtx.stroke();
  }

  function drawSkeletonPath(pts: Array<Point>, opacity: number) {
    if (pts.length < 2) {
      return;
    }

    ctx.strokeStyle = `rgba(${colorToRgbComponents(skeletonColor)},${opacity})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(pts[0]!.x * scale + offsetX, pts[0]!.y * scale + offsetY);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(pts[i]!.x * scale + offsetX, pts[i]!.y * scale + offsetY);
    }
    ctx.stroke();
  }

  function drawSkeleton() {
    if (skeletonColor === "transparent") {
      return;
    }

    if (engine.morphAlpha !== null) {
      // Draw the live lerped skeleton every frame so it always matches the current
      // scale/offset and correctly tracks live curves that change with actualTime
      drawSkeletonPath(engine.getSarmalSkeleton(), DEFAULT_SKELETON_OPACITY);
      return;
    }

    if (engine.isLiveSkeleton) {
      if (skeleton.length < 2) {
        return;
      }

      ctx.strokeStyle = `rgba(${colorToRgbComponents(skeletonColor)},${DEFAULT_SKELETON_OPACITY})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();

      const first = skeleton[0]!;
      ctx.moveTo(first.x * scale + offsetX, first.y * scale + offsetY);

      for (let i = 1; i < skeleton.length; i++) {
        const p = skeleton[i]!;
        ctx.lineTo(p.x * scale + offsetX, p.y * scale + offsetY);
      }

      ctx.stroke();
    } else if (skeletonCanvas) {
      /**
       * ! The offscreen buffer is *already* DPR-scaled, but the main ctx also has a DPR transform applied,
       * ! so passing natural pixel size would **double-scale** it.
       */
      ctx.drawImage(skeletonCanvas, 0, 0, logicalWidth, logicalHeight);
    }
  }

  function drawTrail() {
    if (trailCount < 2) {
      return;
    }

    /**
     * Ribbon approach: draw the trail as a sequence of filled quads.
     * Each quad connects two consecutive trail points with left/right offsets
     * based on the width at that position. Quads are drawn from tail to head
     * so later quads naturally overlay earlier ones.
     *
     * @see https://cesium.com/blog/2013/04/22/robust-polyline-rendering-with-webgl
     *      Cesium: "draw a screen-aligned quad for each segment... extrude them
     *      in screen space in the direction normal to the line."
     */
    const toX = (p: { x: number }) => p.x * scale + offsetX;
    const toY = (p: { y: number }) => p.y * scale + offsetY;
    for (let i = 0; i < trailCount - 1; i++) {
      const { l0x, l0y, r0x, r0y, l1x, l1y, r1x, r1y, opacity, progress } = computeTrailQuad(
        trail,
        i,
        trailCount,
        toX,
        toY,
        TRAIL_MIN_WIDTH * trailWidth,
        TRAIL_MAX_WIDTH * trailWidth,
      );

      // Determine fill color based on trail style
      if (trailStyle === "default") {
        ctx.fillStyle = `rgba(${trailSolidRgb},${opacity})`;
      } else {
        const timeOffset = trailStyle === "gradient-animated" ? gradientAnimTime * 0.0005 : 0;
        const { r, g, b } = oklabToRgb(getPaletteColor(trailPaletteOklab, progress, timeOffset));
        ctx.fillStyle = `rgba(${r},${g},${b},${opacity})`;
      }

      ctx.beginPath();
      ctx.moveTo(l0x, l0y);
      ctx.lineTo(l1x, l1y);
      ctx.lineTo(r1x, r1y);
      ctx.lineTo(r0x, r0y);
      ctx.closePath();
      ctx.fill();
    }
  }

  function drawHead() {
    if (!head) {
      return;
    }

    const x = head.x * scale + offsetX;
    const y = head.y * scale + offsetY;
    const r = headRadius;

    ctx.fillStyle = headColor;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  function renderFrame(deltaTime: number) {
    // Update gradient animation time for animated trail style
    if (trailStyle === "gradient-animated") {
      gradientAnimTime += deltaTime * 1000; // Convert to ms for consistent speed
    }

    if (engine.morphAlpha !== null) {
      morphProgress = Math.min(1, morphProgress + deltaTime / (morphDurationMs / 1000));
      // Drive the blend with the eased progress
      // Keep raw `morphProgress` for completion timing.
      engine.setMorphAlpha(morphEasingFn(morphProgress));

      /**
       * Compute bounds from the actual interpolated skeleton during morph
       * ! This ensures the curve stays properly centered and scaled
       */
      const interpolatedSkeleton = engine.getSarmalSkeleton();
      const bounds = computeBoundaries(interpolatedSkeleton, logicalWidth, logicalHeight);

      if (bounds) {
        scale = bounds.scale;
        offsetX = bounds.offsetX;
        offsetY = bounds.offsetY;
      }

      if (morphProgress >= 1) {
        engine.completeMorph();
        morphResolve?.();
        morphResolve = null;
        morphReject = null;
        morphProgress = 0;

        skeleton = engine.getSarmalSkeleton();
        if (!engine.isLiveSkeleton && skeletonColor !== "transparent") {
          buildSkeletonCanvas();
        }
      }
    }

    trail = engine.tick(deltaTime);
    trailCount = engine.trailCount;
    head = trailCount > 0 ? trail[trailCount - 1]! : null;

    ctx.clearRect(0, 0, logicalWidth, logicalHeight);

    if (engine.isLiveSkeleton && engine.morphAlpha === null) {
      skeleton = engine.getSarmalSkeleton();
      calculateBoundaries();
    }

    drawSkeleton();
    drawTrail();
    drawHead();
  }

  function loop() {
    const now = performance.now();
    const deltaTime = Math.min((now - lastTime) / 1000, 1 / 30);
    lastTime = now;

    renderFrame(deltaTime);
    animationId = requestAnimationFrame(loop);
  }

  // Initialize skeleton and offscreen canvas on creation
  skeleton = engine.getSarmalSkeleton();
  calculateBoundaries();

  if (!engine.isLiveSkeleton && skeletonColor !== "transparent") {
    buildSkeletonCanvas();
  }

  // Handle initialPhase option: seek to the specified position before first frame
  if (options.initialPhase !== undefined) {
    engine.seek(options.initialPhase);
  }

  // Draw initial frame unconditionally (shows skeleton and initial position)
  renderFrame(0);

  // Handle autoStart option: start the animation loop unless explicitly disabled
  const shouldAutoStart = options.autoStart !== false;

  const instance = {
    play() {
      if (destroyed) {
        throw new Error(DESTROYED_ERROR);
      }

      if (animationId !== null) {
        return;
      }

      lastTime = performance.now();
      loop();
    },

    pause() {
      if (destroyed) {
        throw new Error(DESTROYED_ERROR);
      }

      if (animationId === null) {
        return;
      }

      cancelAnimationFrame(animationId);
      animationId = null;
      engine.cancelSpeedTransition();
    },

    reset() {
      if (destroyed) {
        throw new Error(DESTROYED_ERROR);
      }

      engine.reset();
      trail = [];
      head = null;
    },

    destroy() {
      if (destroyed) {
        return;
      }

      destroyed = true;

      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange);

      engine.cancelSpeedTransition();

      if (morphReject !== null) {
        morphReject(new Error("[sarmal] Instance destroyed during morph"));
        morphResolve = null;
        morphReject = null;
      }

      ctx.clearRect(0, 0, logicalWidth, logicalHeight);
    },

    ...enginePassthroughs(engine, () => destroyed),

    morphTo(target: CurveDef, options?: MorphOptions): Promise<void> {
      if (destroyed) {
        throw new Error(DESTROYED_ERROR);
      }

      if (morphResolve !== null) {
        engine.completeMorph();
        morphResolve();
        morphResolve = null;
        morphReject = null;
        morphProgress = 0;
      }

      morphDurationMs = options?.duration ?? DEFAULT_MORPH_DURATION_MS;
      morphEasingFn = options?.easing ?? easeInOutCubic;
      morphProgress = 0;

      engine.startMorph(target, options?.morphStrategy, options?.align ?? false);

      return new Promise<void>((resolve, reject) => {
        morphResolve = resolve;
        morphReject = reject;
      });
    },

    setRenderOptions(partial: RuntimeRenderOptions): void {
      if (destroyed) {
        throw new Error(DESTROYED_ERROR);
      }

      validateRenderOptions(partial);

      if (partial.trailColor !== undefined) {
        trailColor = partial.trailColor;
        trailSolidRgb = colorToRgbComponents(resolveTrailMainColor(trailColor));
        trailPalette = resolveTrailPalette(trailColor);
        trailPaletteOklab = trailPalette.map((c) => parseColorToOklab(c)!);
      }

      if (partial.skeletonColor !== undefined) {
        skeletonColor = partial.skeletonColor;

        if (skeletonColor !== "transparent" && !engine.isLiveSkeleton) {
          buildSkeletonCanvas();
        }
      }

      if (partial.trailStyle !== undefined) {
        trailStyle = partial.trailStyle;
      }

      if (partial.headColor !== undefined) {
        userHeadColor = partial.headColor;
      }

      if (partial.headRadius !== undefined) {
        headRadius = partial.headRadius;
      }

      if (partial.trailWidth !== undefined) {
        trailWidth = partial.trailWidth;
      }

      if (userHeadColor === null) {
        headColor = resolveHeadColor(trailColor, trailStyle);
      } else {
        headColor = userHeadColor;
      }

      if (partial.trailColor !== undefined || partial.trailStyle !== undefined) {
        warnIfTrailColorMismatch(trailColor, trailStyle);
      }
    },
  };

  const pauseOnHidden = options.pauseOnHidden !== false;

  function handleVisibilityChange() {
    if (document.hidden) {
      if (animationId !== null) {
        instance.pause();
        pausedByVisibility = true;
      }
    } else {
      if (pausedByVisibility) {
        pausedByVisibility = false;
        instance.play();
      }
    }
  }

  if (pauseOnHidden) {
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }

  // If the tab is already hidden at construction time, skip auto-start.
  // The visibilitychange listener will resume when the tab becomes visible.
  const actuallyAutoStart = shouldAutoStart && !(pauseOnHidden && document.hidden);
  if (actuallyAutoStart) {
    instance.play();
  } else if (shouldAutoStart) {
    pausedByVisibility = true;
  }

  return instance;
}
